import { createFileRoute, Link } from '@tanstack/react-router';
import { formatTimeSeconds } from '@/lib/dates';
import { useState } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  Zap,
  XCircle,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Copy,
  ExternalLink,
} from 'lucide-react';
import {
  usePipelineExecution,
  useTaskExecutions,
  useAuditLog,
  usePollQueue,
  useCancelExecution,
  useExecutionRealtimeUpdates,
  StatusBadge,
  ActionButton,
  JsonViewer,
  type PipelineTaskExecution,
} from '@/components/hermes';
import { toast } from 'sonner';

export const Route = createFileRoute('/hermes/executions_/$executionId')({
  component: ExecutionDetailPage,
});

function ExecutionDetailPage() {
  const { executionId } = Route.useParams();
  const [selectedTask, setSelectedTask] = useState<PipelineTaskExecution | null>(null);

  const { data: execution, isLoading } = usePipelineExecution(executionId);
  const { data: tasks } = useTaskExecutions(executionId);
  const { data: auditLog } = useAuditLog(execution?.correlation_id ?? '');

  const pollMutation = usePollQueue();
  const cancelMutation = useCancelExecution();

  // Enable realtime updates
  useExecutionRealtimeUpdates();

  const handlePoll = async () => {
    const result = await pollMutation.mutateAsync();
    toast.success(`Processed ${result.processed} tasks`);
  };

  const handleCancel = async () => {
    await cancelMutation.mutateAsync(executionId);
    toast.success('Execution cancelled');
  };

  const copyCorrelationId = () => {
    if (execution?.correlation_id) {
      navigator.clipboard.writeText(execution.correlation_id);
      toast.success('Copied correlation ID');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <p className="text-foreground font-medium">Execution not found</p>
        <Link to="/hermes/executions" className="text-primary hover:underline mt-2">
          Back to executions
        </Link>
      </div>
    );
  }

  const completedTasks = tasks?.filter(t => t.status === 'completed').length ?? 0;
  const totalTasks = tasks?.length ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <Link to="/hermes" className="hover:text-foreground">Hermes</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/hermes/executions" className="hover:text-foreground">Executions</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-foreground">{execution.pipeline_definitions?.name}</span>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              {execution.pipeline_definitions?.name ?? 'Unknown Pipeline'}
              <StatusBadge status={execution.status} animated={execution.status === 'running' || execution.status === 'in_progress'} />
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <code className="text-xs text-muted-foreground font-mono bg-secondary px-2 py-1 rounded">
                {execution.correlation_id ?? execution.id}
              </code>
              <button
                onClick={copyCorrelationId}
                className="p-1 rounded hover:bg-secondary transition-colors"
                title="Copy correlation ID"
              >
                <Copy className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ActionButton onClick={handlePoll} size="sm">
              <Zap className="w-3.5 h-3.5" />
              Poll
            </ActionButton>
            {(execution.status === 'running' || execution.status === 'in_progress') && (
              <ActionButton onClick={handleCancel} size="sm" variant="destructive">
                <XCircle className="w-3.5 h-3.5" />
                Cancel
              </ActionButton>
            )}
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Status"
          value={execution.status === 'in_progress' ? 'in progress' : execution.status}
          icon={execution.status === 'running' || execution.status === 'in_progress' ? RefreshCw : execution.status === 'completed' ? CheckCircle2 : Clock}
        />
        <StatCard
          label="Progress"
          value={`${completedTasks}/${totalTasks} tasks`}
          icon={CheckCircle2}
        />
        <StatCard
          label="Started"
          value={execution.started_at ? formatTime(execution.started_at) : 'Not started'}
          icon={Clock}
        />
        <StatCard
          label="Duration"
          value={calculateDuration(execution.started_at, execution.completed_at)}
          icon={Clock}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-secondary/50">
              <h2 className="font-semibold text-foreground">Task Executions</h2>
            </div>
            <div className="divide-y divide-border">
              {tasks?.map((task) => (
                <div
                  key={task.id}
                  onClick={() => setSelectedTask(task)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    selectedTask?.id === task.id ? 'bg-primary/10' : 'hover:bg-secondary/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TaskStatusIcon status={task.status} />
                      <div>
                        <span className="font-medium text-foreground">{task.task_name}</span>
                        {task.attempt_count > 1 && (
                          <span className="ml-2 text-xs text-amber-400">
                            ({task.attempt_count} attempts)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={task.status} animated={task.status === 'running' || task.status === 'in_progress'} size='sm' />
                      <span className="text-xs text-muted-foreground">
                        {calculateDuration(task.started_at, task.completed_at)}
                      </span>
                    </div>
                  </div>
                  {task.error_message && (
                    <p className="mt-2 text-xs text-red-400 truncate">
                      {task.error_message}
                    </p>
                  )}
                </div>
              ))}
              {(!tasks || tasks.length === 0) && (
                <div className="px-4 py-8 text-center text-muted-foreground">
                  No tasks yet
                </div>
              )}
            </div>
          </div>

          {/* Input Data */}
          {execution.input_data && (
            <JsonViewer
              data={execution.input_data}
              title="Input Data"
              defaultExpanded={false}
            />
          )}
        </div>

        {/* Task Detail / Audit Log Sidebar */}
        <div className="space-y-4">
          {selectedTask ? (
            <TaskDetailPanel task={selectedTask} onClose={() => setSelectedTask(null)} />
          ) : (
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-sm text-muted-foreground text-center">
                Click a task to view details
              </p>
            </div>
          )}

          {/* Audit Log */}
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-secondary/50">
              <h2 className="font-semibold text-foreground">Audit Log</h2>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {auditLog?.map((entry) => (
                <div key={entry.id} className="px-4 py-2 border-b border-border last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-foreground">
                      {entry.event_type}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatTime(entry.created_at)}
                    </span>
                  </div>
                </div>
              ))}
              {(!auditLog || auditLog.length === 0) && (
                <div className="px-4 py-4 text-center text-muted-foreground text-sm">
                  No audit entries
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper Components

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ElementType;
}

function StatCard({ label, value, icon: Icon }: StatCardProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-muted-foreground" />
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-sm font-medium text-foreground capitalize">{value}</p>
        </div>
      </div>
    </div>
  );
}

function TaskStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'running':
    case 'in_progress':
      return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />;
    case 'completed':
      return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-400" />;
    case 'stale':
      return <AlertTriangle className="w-4 h-4 text-orange-400" />;
    default:
      return <Clock className="w-4 h-4 text-muted-foreground" />;
  }
}

interface TaskDetailPanelProps {
  task: PipelineTaskExecution;
  onClose: () => void;
}

function TaskDetailPanel({ task, onClose }: TaskDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'input' | 'output' | 'error'>('input');

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-secondary/50 flex items-center justify-between">
        <h3 className="font-semibold text-foreground">{task.task_name}</h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-secondary transition-colors"
        >
          <XCircle className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Task Info */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status</span>
            <StatusBadge status={task.status} size="sm" />
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Attempts</span>
            <span className="text-foreground">{task.attempt_count}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Duration</span>
            <span className="text-foreground">
              {calculateDuration(task.started_at, task.completed_at)}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border">
          <TabButton active={activeTab === 'input'} onClick={() => setActiveTab('input')}>
            Input
          </TabButton>
          <TabButton active={activeTab === 'output'} onClick={() => setActiveTab('output')}>
            Output
          </TabButton>
          {task.error_message && (
            <TabButton active={activeTab === 'error'} onClick={() => setActiveTab('error')}>
              Error
            </TabButton>
          )}
        </div>

        {/* Tab Content */}
        <div className="max-h-[300px] overflow-auto">
          {activeTab === 'input' && (
            <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
              {JSON.stringify(task.input_data, null, 2) || 'No input data'}
            </pre>
          )}
          {activeTab === 'output' && (
            <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
              {JSON.stringify(task.output_data, null, 2) || 'No output data'}
            </pre>
          )}
          {activeTab === 'error' && task.error_message && (
            <div className="text-xs text-red-400 font-mono whitespace-pre-wrap">
              {task.error_message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
        active
          ? 'text-primary border-primary'
          : 'text-muted-foreground border-transparent hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function formatTime(dateStr: string): string {
  return formatTimeSeconds(dateStr);
}

function calculateDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '-';

  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const diffMs = end.getTime() - start.getTime();

  if (diffMs < 1000) return `${diffMs}ms`;
  if (diffMs < 60000) return `${(diffMs / 1000).toFixed(1)}s`;
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ${Math.floor((diffMs % 60000) / 1000)}s`;

  const hours = Math.floor(diffMs / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);
  return `${hours}h ${mins}m`;
}
